/**
 * Vendor Bot Shop - Checkout Page
 * Dynamic multi-step checkout based on product types
 * Physical: review → shipping_address → shipping_method → payment → confirm
 * Digital: review → payment → confirm
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { BottomNavigation } from '../../components/customer';
import { useTranslation } from '../../hooks/useTranslation';

interface CartItem {
  id: number;
  product_id: number;
  name: string;
  price: number;
  quantity: number;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  image_url: string | null;
}

interface StoreInfo {
  store_name: string;
  currency: string;
  currency_symbol: string;
}

interface CryptoCoin {
  symbol: string;
  source: 'gateway' | 'wallet_pool';
  gateway?: string;
  gatewayName?: string;
  walletAddress?: string;
  network?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
  type: string;
  icon: string;
  description: string;
  coins?: CryptoCoin[];
  activeGateway?: string;
  gatewayName?: string;
}

interface ShippingOption {
  type: 'standard' | 'express';
  label: string;
  price: number;
}

interface ShippingMethod {
  id: number;
  name: string;
  description: string;
  options: ShippingOption[];
}

interface CouponInfo {
  couponCode: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  discountAmount: number;
}

type CheckoutStep = 'review' | 'shipping_address' | 'shipping_method' | 'payment' | 'confirm';

const Checkout: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    store_name: 'Store',
    currency: 'USD',
    currency_symbol: '$'
  });
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Checkout state
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('review');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [selectedCryptoCoin, setSelectedCryptoCoin] = useState<string | null>(null);

  // Shipping address - single textarea
  const [shippingAddress, setShippingAddress] = useState<string>('');

  // Shipping method selection
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<number | null>(null);
  const [selectedShippingType, setSelectedShippingType] = useState<'standard' | 'express'>('standard');

  // Coupon state
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
  const [couponError, setCouponError] = useState<string>('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);


  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Determine if shipping is needed
  const hasPhysicalProducts = cartItems.some(item => item.product_type === 'physical');
  const hasDigitalProducts = cartItems.some(item =>
    ['downloadable', 'keys', 'subscription'].includes(item.product_type)
  );

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Get shipping price from selected method
  const getShippingPrice = useCallback((): number => {
    if (!hasPhysicalProducts || !selectedShippingMethodId) return 0;
    const method = shippingMethods.find(m => m.id === selectedShippingMethodId);
    if (!method) return 0;
    const option = method.options.find(o => o.type === selectedShippingType);
    return option?.price || 0;
  }, [hasPhysicalProducts, selectedShippingMethodId, selectedShippingType, shippingMethods]);

  const shippingPrice = getShippingPrice();
  const discountAmount = couponInfo ? couponInfo.discountAmount : 0;
  const total = subtotal + shippingPrice - discountAmount;

  // Define checkout steps based on cart contents
  const getSteps = (): CheckoutStep[] => {
    if (hasPhysicalProducts) {
      return ['review', 'shipping_address', 'shipping_method', 'payment', 'confirm'];
    }
    return ['review', 'payment', 'confirm'];
  };

  const steps = getSteps();
  const currentStepIndex = steps.indexOf(currentStep);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('auth_token');

      try {
        // Load store info, cart, and payment methods in parallel
        const [storeRes, cartRes, paymentRes] = await Promise.all([
          fetch(`${baseUrl}/api/shop/${botUsername}/store-info`),
          fetch(`${baseUrl}/api/cart/${botUsername}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${baseUrl}/api/shop/${botUsername}/payment-methods`)
        ]);

        const [storeData, cartData, paymentData] = await Promise.all([
          storeRes.json(),
          cartRes.json(),
          paymentRes.json()
        ]);

        if (storeData.success) setStoreInfo(storeData.data);
        if (cartData.success) {
          const items = cartData.data?.items || cartData.data || [];
          setCartItems(items);

          // Check if physical products exist, then fetch shipping methods
          const hasPhysical = items.some((item: CartItem) => item.product_type === 'physical');

          if (hasPhysical) {
            try {
              const shippingRes = await fetch(`${baseUrl}/api/shop/${botUsername}/shipping-methods`);
              const shippingData = await shippingRes.json();
              if (shippingData.success && shippingData.data?.length > 0) {
                setShippingMethods(shippingData.data);
                // Auto-select first shipping method
                setSelectedShippingMethodId(shippingData.data[0].id);
              }
            } catch (e) {
              console.error('Error loading shipping methods:', e);
            }
          }
        }
        if (paymentData.success) setPaymentMethods(paymentData.data || []);

      } catch (error) {
        console.error('Error loading checkout data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [botUsername, baseUrl]);

  // Redirect if cart is empty
  useEffect(() => {
    if (!isLoading && cartItems.length === 0) {
      navigate(`/shop/${botUsername}/cart`);
    }
  }, [isLoading, cartItems, navigate, botUsername]);

  // Initialize Telegram WebApp
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();

      if (tg.themeParams?.bg_color) {
        tg.setBackgroundColor(tg.themeParams.bg_color);
        tg.setHeaderColor(tg.themeParams.bg_color);
      }
    }
  }, []);

  // Handle step navigation
  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    } else {
      navigate(`/shop/${botUsername}/cart`);
    }
  };

  // Validate current step
  const isStepValid = (): boolean => {
    switch (currentStep) {
      case 'review':
        return cartItems.length > 0;
      case 'shipping_address':
        // Single textarea must have at least 10 characters
        return shippingAddress.trim().length >= 10;
      case 'shipping_method':
        return selectedShippingMethodId !== null;
      case 'payment':
        if (!selectedPaymentMethod) return false;
        // If crypto is selected and has coins, require coin selection
        const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
        if (selectedMethod?.type === 'crypto' && selectedMethod.coins && selectedMethod.coins.length > 0) {
          return !!selectedCryptoCoin;
        }
        return true;
      case 'confirm':
        return true;
      default:
        return false;
    }
  };

  // Get payment method display name (with coin for crypto)
  const getPaymentMethodDisplayName = (): string => {
    const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
    if (!method) return t('common.none');
    if (method.type === 'crypto' && selectedCryptoCoin) {
      return `${method.name}: ${selectedCryptoCoin}`;
    }
    return method.name;
  };

  // Handle coupon validation
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setIsValidatingCoupon(true);
    setCouponError('');
    setCouponInfo(null);

    const tg = window.Telegram?.WebApp;
    const initData = tg?.initData || '';

    try {
      const response = await fetch(`${baseUrl}/api/shop/${botUsername}/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-Init-Data': initData
        },
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          subtotal
        })
      });

      const data = await response.json();

      if (data.success && data.valid) {
        setCouponInfo(data.data);
        setCouponError('');
        tg?.HapticFeedback?.notificationOccurred('success');
      } else {
        setCouponError(data.error || t('checkout.invalidCoupon'));
        setCouponInfo(null);
        tg?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (error) {
      setCouponError(t('checkout.invalidCoupon'));
      setCouponInfo(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponInfo(null);
    setCouponError('');
  };

  // Place order
  const handlePlaceOrder = async () => {
    setIsProcessing(true);
    const token = localStorage.getItem('auth_token');
    const tg = window.Telegram?.WebApp;

    try {
      // Build order payload
      const orderPayload: any = {
        paymentMethod: selectedPaymentMethod,
        couponCode: couponInfo?.couponCode || null
      };

      // Add crypto coin if selected
      if (selectedCryptoCoin) {
        orderPayload.cryptoCoin = selectedCryptoCoin;
      }

      if (hasPhysicalProducts) {
        orderPayload.shippingAddress = shippingAddress;
        orderPayload.shippingMethodId = selectedShippingMethodId;
        orderPayload.shippingMethodType = selectedShippingType;
      }

      const orderRes = await fetch(`${baseUrl}/api/orders/${botUsername}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderPayload)
      });

      const orderData = await orderRes.json();

      if (!orderData.success) {
        throw new Error(orderData.error || t('checkout.failedToCreateOrder'));
      }

      // Initialize payment
      const paymentPayload: any = {
        order_id: orderData.data.orderId,
        payment_method: selectedPaymentMethod
      };

      // Add crypto coin if crypto payment selected
      if (selectedPaymentMethod === 'crypto' && selectedCryptoCoin) {
        paymentPayload.crypto_coin = selectedCryptoCoin;
      }

      const paymentRes = await fetch(`${baseUrl}/api/payment/${botUsername}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(paymentPayload)
      });

      const paymentData = await paymentRes.json();

      if (!paymentData.success) {
        throw new Error(paymentData.error || t('checkout.failedToInitPayment'));
      }

      // Handle different payment flows
      const data = paymentData.data;
      const orderNum = data.orderNumber || orderData.data.orderNumber;
      const amount = data.fiatAmount?.toString() || data.amount?.toString() || total.toString();

      if (data?.pay_url) {
        // CryptoBot or other gateway with payment URL
        const params = new URLSearchParams({
          order: orderNum,
          fiatAmount: amount,
          method: 'crypto',
          coin: data.coin || selectedCryptoCoin || '',
          gateway: data.gateway || '',
          gatewayPaymentId: data.gatewayPaymentId || '',
          payUrl: data.pay_url,
          status: 'pending'
        });
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      } else if (data?.redirect_url) {
        // External payment (Stripe, PayPal, etc.)
        window.location.href = data.redirect_url;
      } else if (data?.status === 'completed') {
        // Wallet or instant payment - go to success page
        const params = new URLSearchParams({
          order: orderNum,
          amount: amount,
          method: data.paymentMethod || selectedPaymentMethod || '',
          status: 'completed'
        });
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      } else if (data?.payment_address) {
        // Crypto payment with wallet address - show payment page
        const params = new URLSearchParams({
          order: orderNum,
          fiatAmount: amount,
          cryptoAmount: data.formattedCryptoAmount || data.cryptoAmount?.toString() || '',
          method: 'crypto',
          coin: data.coin || selectedCryptoCoin || '',
          address: data.payment_address,
          network: data.network || '',
          status: 'pending'
        });
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      } else if (data?.paymentMethod === 'telegram_stars') {
        // Telegram Stars payment - invoice sent to bot chat
        const params = new URLSearchParams({
          order: orderNum,
          amount: amount,
          method: 'telegram_stars',
          starsAmount: data.starsAmount?.toString() || '',
          status: 'pending'
        });
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      } else if (data?.paymentMethod === 'custom') {
        // Custom payment method with instructions
        const params = new URLSearchParams({
          order: orderNum,
          amount: amount,
          method: 'custom',
          methodName: data.methodName || '',
          instruction: data.instruction || '',
          status: 'pending'
        });
        if (data.qrCode) params.set('qrCode', data.qrCode);
        if (data.link) params.set('link', data.link);
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      } else {
        // Fallback for other pending payments
        const params = new URLSearchParams({
          order: orderNum,
          amount: amount,
          method: data.paymentMethod || selectedPaymentMethod || '',
          status: data.status || 'pending'
        });
        navigate(`/shop/${botUsername}/payment?${params.toString()}`);
      }
    } catch (error: any) {
      console.error('Error placing order:', error);
      const tg = window.Telegram?.WebApp;
      tg?.showAlert?.(error.message || t('checkout.failedToPlaceOrder'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Format price
  const formatPrice = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Get step title
  const getStepTitle = (step: CheckoutStep): string => {
    switch (step) {
      case 'review': return t('checkout.reviewOrder');
      case 'shipping_address': return t('checkout.shippingAddress');
      case 'shipping_method': return t('checkout.shippingMethod');
      case 'payment': return t('checkout.payment');
      case 'confirm': return t('checkout.confirm');
      default: return '';
    }
  };

  // Payment method icon mapping
  const getPaymentIcon = (type: string): string => {
    switch (type) {
      case 'wallet': return 'wallet';
      case 'stripe': return 'card';
      case 'paypal': return 'paypal';
      case 'nowpayments':
      case 'coinpayments':
      case 'cryptobot':
      case 'mixpay':
        return 'crypto';
      case 'telegram_stars': return 'stars';
      case 'flutterwave':
      case 'opay':
        return 'mobile';
      default: return 'card';
    }
  };

  // Get translated payment method name (preserves custom method names from database)
  const getPaymentMethodName = (method: PaymentMethod): string => {
    switch (method.type) {
      case 'wallet': return t('payment.walletBalance');
      case 'telegram_stars': return t('payment.stars');
      case 'nowpayments':
      case 'coinpayments':
      case 'cryptobot':
      case 'mixpay':
      case 'crypto':
        return t('payment.cryptocurrency');
      case 'stripe':
      case 'paypal':
      case 'flutterwave':
      case 'opay':
        return t('payment.card');
      default:
        // For custom payment methods, use the original name from database
        return method.name;
    }
  };

  // Get translated payment method description (preserves custom method descriptions)
  const getPaymentMethodDescription = (method: PaymentMethod): string => {
    switch (method.type) {
      case 'wallet': return t('payment.walletDescription');
      case 'telegram_stars': return t('payment.starsDescription');
      case 'nowpayments':
      case 'coinpayments':
      case 'cryptobot':
      case 'mixpay':
      case 'crypto':
        return t('payment.cryptoDescription');
      case 'stripe':
      case 'paypal':
        return t('payment.cardDescription');
      case 'flutterwave':
      case 'opay':
        return t('payment.mobileDescription');
      default:
        // For custom payment methods, use the original description from database
        return method.description || '';
    }
  };

  // Get selected shipping method name
  const getSelectedShippingMethodName = (): string => {
    const method = shippingMethods.find(m => m.id === selectedShippingMethodId);
    if (!method) return '';
    const typeLabel = selectedShippingType === 'express'
      ? t('checkout.expressShipping')
      : t('checkout.standardShipping');
    return `${method.name} - ${typeLabel}`;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate(`/shop/${botUsername}/cart`)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </BackButton>
          <HeaderTitle>{t('checkout.title')}</HeaderTitle>
        </Header>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
        <BottomNavigation />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={goToPrevStep}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </BackButton>
        <HeaderTitle>{getStepTitle(currentStep)}</HeaderTitle>
      </Header>

      <StepIndicator>
        {steps.map((step, index) => (
          <StepDot
            key={step}
            $active={index <= currentStepIndex}
            $current={step === currentStep}
          />
        ))}
      </StepIndicator>

      <Content>
        {/* Step 1: Review */}
        {currentStep === 'review' && (
          <StepContent>
            <SectionTitle>{t('checkout.orderItems')}</SectionTitle>
            <ItemsList>
              {cartItems.map(item => (
                <OrderItem key={item.id}>
                  <ItemImageContainer>
                    {item.image_url ? (
                      <ItemImage src={item.image_url} alt={item.name} />
                    ) : (
                      <PlaceholderImage>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
                        </svg>
                      </PlaceholderImage>
                    )}
                  </ItemImageContainer>
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemMeta>{t('cart.quantity')}: {item.quantity}</ItemMeta>
                  </ItemInfo>
                  <ItemPrice>
                    {storeInfo.currency_symbol}{formatPrice(item.price * item.quantity)}
                  </ItemPrice>
                </OrderItem>
              ))}
            </ItemsList>

            {hasDigitalProducts && (
              <InfoBanner $type="info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <span>{t('checkout.digitalDelivery')}</span>
              </InfoBanner>
            )}

            <SummaryCard>
              <SummaryRow>
                <span>{t('cart.subtotal')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(subtotal)}</span>
              </SummaryRow>
              {hasPhysicalProducts && (
                <SummaryRow>
                  <span>{t('cart.shipping')}</span>
                  <span>{t('checkout.calculatedNext')}</span>
                </SummaryRow>
              )}
              <TotalRow>
                <span>{t('cart.total')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(subtotal)}</span>
              </TotalRow>
            </SummaryCard>
          </StepContent>
        )}

        {/* Step 2: Shipping Address (single textarea) */}
        {currentStep === 'shipping_address' && (
          <StepContent>
            <SectionTitle>{t('checkout.deliveryAddress')}</SectionTitle>

            <InfoBanner $type="info">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span>{t('checkout.addressHint')}</span>
            </InfoBanner>

            <FormGroup>
              <FormLabel>{t('checkout.deliveryAddress')} *</FormLabel>
              <AddressTextarea
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder={t('checkout.addressPlaceholder')}
                rows={6}
              />
              <FormHint>{t('checkout.addressFormatHint')}</FormHint>
            </FormGroup>
          </StepContent>
        )}

        {/* Step 3: Shipping Method Selection */}
        {currentStep === 'shipping_method' && (
          <StepContent>
            <SectionTitle>{t('checkout.selectShippingMethod')}</SectionTitle>

            {shippingMethods.length === 0 ? (
              <EmptyState>
                <p>{t('checkout.noShippingMethods')}</p>
              </EmptyState>
            ) : (
              <ShippingMethodsList>
                {shippingMethods.map(method => (
                  <ShippingMethodCard
                    key={method.id}
                    $selected={selectedShippingMethodId === method.id}
                    onClick={() => setSelectedShippingMethodId(method.id)}
                  >
                    <ShippingMethodHeader>
                      <ShippingMethodName>{method.name}</ShippingMethodName>
                      {selectedShippingMethodId === method.id && (
                        <CheckIcon>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </CheckIcon>
                      )}
                    </ShippingMethodHeader>

                    {method.description && (
                      <ShippingMethodDescription>{method.description}</ShippingMethodDescription>
                    )}

                    {selectedShippingMethodId === method.id && method.options.length > 0 && (
                      <ShippingOptions>
                        {method.options.map(option => (
                          <ShippingOptionRow
                            key={option.type}
                            $selected={selectedShippingType === option.type}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedShippingType(option.type);
                            }}
                          >
                            <RadioCircle $selected={selectedShippingType === option.type} />
                            <OptionLabel>
                              {option.type === 'express' ? t('checkout.expressShipping') : t('checkout.standardShipping')}
                            </OptionLabel>
                            <OptionPrice>
                              {option.price > 0
                                ? `${storeInfo.currency_symbol}${formatPrice(option.price)}`
                                : t('checkout.free')
                              }
                            </OptionPrice>
                          </ShippingOptionRow>
                        ))}
                      </ShippingOptions>
                    )}
                  </ShippingMethodCard>
                ))}
              </ShippingMethodsList>
            )}
          </StepContent>
        )}

        {/* Step 4: Payment (with coupon) */}
        {currentStep === 'payment' && (
          <StepContent>
            {/* Coupon Section */}
            <SectionTitle>{t('checkout.couponCode')}</SectionTitle>
            <CouponSection>
              <CouponInputWrapper>
                <CouponInput
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder={t('checkout.enterCouponCode')}
                  disabled={!!couponInfo}
                />
                {!couponInfo ? (
                  <ApplyCouponButton
                    onClick={handleApplyCoupon}
                    disabled={isValidatingCoupon || !couponCode.trim()}
                  >
                    {isValidatingCoupon ? '...' : t('checkout.apply')}
                  </ApplyCouponButton>
                ) : (
                  <RemoveCouponButton onClick={handleRemoveCoupon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </RemoveCouponButton>
                )}
              </CouponInputWrapper>

              {couponError && <CouponError>{couponError}</CouponError>}

              {couponInfo && (
                <CouponSuccess>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span>
                    {couponInfo.discountType === 'percentage'
                      ? `${couponInfo.discountValue}% ${t('checkout.discountApplied')}`
                      : `${storeInfo.currency_symbol}${couponInfo.discountValue} ${t('checkout.discountApplied')}`
                    }
                  </span>
                </CouponSuccess>
              )}
            </CouponSection>

            <Separator />

            {/* Payment Methods Section */}
            <SectionTitle>{t('checkout.selectPaymentMethod')}</SectionTitle>
            <PaymentMethodsList>
              {paymentMethods.map(method => (
                <React.Fragment key={method.id}>
                  <PaymentMethodCard
                    $selected={selectedPaymentMethod === method.id}
                    onClick={() => setSelectedPaymentMethod(method.id)}
                  >
                    <PaymentIcon $type={getPaymentIcon(method.type)}>
                      {getPaymentIcon(method.type) === 'wallet' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                        </svg>
                      )}
                      {getPaymentIcon(method.type) === 'card' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                        </svg>
                      )}
                      {getPaymentIcon(method.type) === 'crypto' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                      )}
                      {getPaymentIcon(method.type) === 'stars' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                      )}
                      {getPaymentIcon(method.type) === 'mobile' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
                        </svg>
                      )}
                      {getPaymentIcon(method.type) === 'paypal' && (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                        </svg>
                      )}
                    </PaymentIcon>
                    <PaymentInfo>
                      <PaymentName>{getPaymentMethodName(method)}</PaymentName>
                      <PaymentDescription>{getPaymentMethodDescription(method)}</PaymentDescription>
                    </PaymentInfo>
                    {selectedPaymentMethod === method.id && (
                      <CheckIcon>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      </CheckIcon>
                    )}
                  </PaymentMethodCard>
                  {/* Coin selection for crypto */}
                  {selectedPaymentMethod === method.id && method.type === 'crypto' && method.coins && method.coins.length > 0 && (
                    <CoinSelectionContainer>
                      <CoinSelectionTitle>{t('checkout.selectCoin')}</CoinSelectionTitle>
                      <CoinsList>
                        {method.coins.map(coin => (
                          <CoinOption
                            key={coin.symbol}
                            $selected={selectedCryptoCoin === coin.symbol}
                            onClick={() => setSelectedCryptoCoin(coin.symbol)}
                          >
                            <CoinName>{coin.symbol}</CoinName>
                            {selectedCryptoCoin === coin.symbol && (
                              <CoinCheck>✓</CoinCheck>
                            )}
                          </CoinOption>
                        ))}
                      </CoinsList>
                    </CoinSelectionContainer>
                  )}
                </React.Fragment>
              ))}
            </PaymentMethodsList>

            {paymentMethods.length === 0 && (
              <EmptyState>
                <p>{t('checkout.noPaymentMethods')}</p>
              </EmptyState>
            )}
          </StepContent>
        )}

        {/* Step 5: Confirm */}
        {currentStep === 'confirm' && (
          <StepContent>
            <SectionTitle>{t('common.orderSummary')}</SectionTitle>
            <SummaryCard>
              <ItemsList $compact>
                {cartItems.map(item => (
                  <OrderItem key={item.id} $compact>
                    <ItemInfo>
                      <ItemName>{item.name}</ItemName>
                      <ItemMeta>{t('cart.quantity')}: {item.quantity}</ItemMeta>
                    </ItemInfo>
                    <ItemPrice>
                      {storeInfo.currency_symbol}{formatPrice(item.price * item.quantity)}
                    </ItemPrice>
                  </OrderItem>
                ))}
              </ItemsList>

              <Separator />

              <SummaryRow>
                <span>{t('cart.subtotal')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(subtotal)}</span>
              </SummaryRow>
              {hasPhysicalProducts && (
                <SummaryRow>
                  <span>{t('cart.shipping')}</span>
                  <span>
                    {shippingPrice > 0
                      ? `${storeInfo.currency_symbol}${formatPrice(shippingPrice)}`
                      : t('checkout.free')
                    }
                  </span>
                </SummaryRow>
              )}
              {couponInfo && (
                <SummaryRow $discount>
                  <span>{t('checkout.discount')} ({couponInfo.couponCode})</span>
                  <span>-{storeInfo.currency_symbol}{formatPrice(discountAmount)}</span>
                </SummaryRow>
              )}
              <TotalRow>
                <span>{t('cart.total')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(total)}</span>
              </TotalRow>
            </SummaryCard>

            {hasPhysicalProducts && (
              <>
                <SectionTitle>{t('checkout.shippingAddress')}</SectionTitle>
                <ConfirmCard>
                  <ConfirmTextPre>{shippingAddress}</ConfirmTextPre>
                </ConfirmCard>

                <SectionTitle>{t('checkout.shippingMethod')}</SectionTitle>
                <ConfirmCard>
                  <ConfirmText>
                    <strong>{getSelectedShippingMethodName()}</strong>
                  </ConfirmText>
                  <ConfirmText>
                    {shippingPrice > 0
                      ? `${storeInfo.currency_symbol}${formatPrice(shippingPrice)}`
                      : t('checkout.free')
                    }
                  </ConfirmText>
                </ConfirmCard>
              </>
            )}

            <SectionTitle>{t('checkout.paymentMethod')}</SectionTitle>
            <ConfirmCard>
              <ConfirmText>
                <strong>
                  {getPaymentMethodDisplayName()}
                </strong>
              </ConfirmText>
            </ConfirmCard>

          </StepContent>
        )}
      </Content>

      <Footer>
        {currentStep !== 'confirm' ? (
          <ContinueButton
            onClick={goToNextStep}
            disabled={!isStepValid()}
          >
            {t('common.continue')}
          </ContinueButton>
        ) : (
          <PlaceOrderButton
            onClick={handlePlaceOrder}
            disabled={isProcessing}
          >
            {isProcessing ? t('checkout.processing') : `${t('checkout.placeOrder')} - ${storeInfo.currency_symbol}${formatPrice(total)}`}
          </PlaceOrderButton>
        )}
      </Footer>

      <BottomNavigation />
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding-bottom: calc(130px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
  }

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.header`
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;

  @media (min-width: 375px) {
    padding: 16px;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--tg-theme-text-color, #000000);
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 18px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: var(--tg-theme-bg-color, #ffffff);

  @media (min-width: 375px) {
    gap: 8px;
    padding: 16px;
  }
`;

const StepDot = styled.div<{ $active: boolean; $current: boolean }>`
  width: ${props => props.$current ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  background: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #e0e0e0)'};
  transition: all 0.3s;
`;

const Content = styled.main`
  padding: 12px;

  @media (min-width: 375px) {
    padding: 16px;
  }

  @media (min-width: 600px) {
    padding: 20px;
  }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 375px) {
    gap: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 16px;
  }
`;

const ItemsList = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$compact ? '8px' : '12px'};
`;

const OrderItem = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  ${props => !props.$compact && `
    background: var(--tg-theme-bg-color, #ffffff);
    padding: 12px;
    border-radius: 10px;
  `}
`;

const ItemImageContainer = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tg-theme-hint-color, #999);
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemMeta = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
`;

const ItemPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const InfoBanner = styled.div<{ $type: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  background: ${props => props.$type === 'info' ? '#2196F320' : '#4CAF5020'};
  color: ${props => props.$type === 'info' ? '#2196F3' : '#4CAF50'};
`;

const SummaryCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 12px;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

const SummaryRow = styled.div<{ $discount?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: ${props => props.$discount ? '#4CAF50' : 'var(--tg-theme-hint-color, #999)'};
  padding: 4px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)40;
`;

const Separator = styled.div`
  height: 1px;
  background: var(--tg-theme-hint-color, #e0e0e0)40;
  margin: 12px 0;
`;

// Address textarea styles
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
`;

const FormHint = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 4px 0 0 0;
`;

const AddressTextarea = styled.textarea`
  background: var(--tg-theme-bg-color, #ffffff);
  border: 1px solid var(--tg-theme-hint-color, #e0e0e0)60;
  border-radius: 12px;
  padding: 14px;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  outline: none;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  min-height: 140px;

  &:focus {
    border-color: var(--tg-theme-button-color, #3390ec);
  }

  &::placeholder {
    color: var(--tg-theme-hint-color, #999);
  }
`;

const FormTextarea = styled.textarea`
  background: var(--tg-theme-bg-color, #ffffff);
  border: 1px solid var(--tg-theme-hint-color, #e0e0e0)60;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  outline: none;
  resize: none;
  font-family: inherit;

  &:focus {
    border-color: var(--tg-theme-button-color, #3390ec);
  }

  &::placeholder {
    color: var(--tg-theme-hint-color, #999);
  }
`;

// Shipping method styles
const ShippingMethodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ShippingMethodCard = styled.div<{ $selected: boolean }>`
  background: var(--tg-theme-bg-color, #ffffff);
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'transparent'};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s;
`;

const ShippingMethodHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ShippingMethodName = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

const ShippingMethodDescription = styled.p`
  font-size: 13px;
  color: var(--tg-theme-hint-color, #999);
  margin: 4px 0 0 0;
`;

const ShippingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)30;
`;

const ShippingOptionRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)10'
    : 'transparent'};
  border-radius: 8px;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #999)'};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.$selected
      ? 'var(--tg-theme-button-color, #3390ec)'
      : 'transparent'};
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
`;

const OptionPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

// Coupon styles
const CouponSection = styled.div`
  margin-bottom: 8px;
`;

const CouponInputWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const CouponInput = styled.input`
  flex: 1;
  background: var(--tg-theme-bg-color, #ffffff);
  border: 1px solid var(--tg-theme-hint-color, #e0e0e0)60;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  text-transform: uppercase;
  outline: none;

  &:focus {
    border-color: var(--tg-theme-button-color, #3390ec);
  }

  &:disabled {
    background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  }

  &::placeholder {
    color: var(--tg-theme-hint-color, #999);
    text-transform: none;
  }
`;

const ApplyCouponButton = styled.button<{ disabled: boolean }>`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  white-space: nowrap;
`;

const RemoveCouponButton = styled.button`
  background: var(--tg-theme-hint-color, #999);
  color: white;
  border: none;
  width: 44px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CouponError = styled.p`
  font-size: 13px;
  color: #f44336;
  margin: 8px 0 0 0;
`;

const CouponSuccess = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4CAF50;
  margin-top: 8px;
`;

// Payment method styles
const PaymentMethodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentMethodCard = styled.div<{ $selected: boolean }>`
  background: var(--tg-theme-bg-color, #ffffff);
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'transparent'};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: border-color 0.2s;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
    gap: 12px;
  }
`;

const PaymentIcon = styled.div<{ $type: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  background: ${props => {
    switch (props.$type) {
      case 'wallet': return '#4CAF50';
      case 'card': return '#2196F3';
      case 'crypto': return '#FF9800';
      case 'stars': return '#9C27B0';
      case 'mobile': return '#E91E63';
      default: return '#757575';
    }
  }};
`;

const PaymentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PaymentName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

const PaymentDescription = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 4px 0 0 0;
`;

const CheckIcon = styled.div`
  color: var(--tg-theme-button-color, #3390ec);
  flex-shrink: 0;
`;

const CoinSelectionContainer = styled.div`
  margin: -8px 0 16px 0;
  padding: 12px 16px;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 0 0 12px 12px;
`;

const CoinSelectionTitle = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 8px 0;
`;

const CoinsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CoinOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-bg-color, #ffffff)'};
  color: ${props => props.$selected
    ? 'var(--tg-theme-button-text-color, #ffffff)'
    : 'var(--tg-theme-text-color, #000000)'};
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #e0e0e0)'};
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
`;

const CoinName = styled.span``;

const CoinCheck = styled.span`
  font-size: 12px;
`;

// Confirm step styles
const ConfirmCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 12px;
  padding: 12px 16px;
`;

const ConfirmText = styled.p`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  margin: 2px 0;
  line-height: 1.4;
`;

const ConfirmTextPre = styled.pre`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
  font-family: inherit;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--tg-theme-hint-color, #999);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-hint-color, #e0e0e0);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Footer = styled.footer`
  position: fixed;
  bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    padding: 16px;
  }

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  transition: opacity 0.2s;

  @media (min-width: 375px) {
    padding: 14px;
    font-size: 16px;
  }
`;

const PlaceOrderButton = styled(ContinueButton)`
  background: #4CAF50;
`;

export default Checkout;
